

import { db } from "../../config/firebase";
import { CLOUD_FUNCTIONS_API_KEY, GCF_GET_OR_CREATE_USER_BY_EMAIL_URL } from "../../config/api";
import { collection, addDoc, getDoc, query, setDoc, where, getDocs, doc, deleteDoc, updateDoc, orderBy, Timestamp } from 'firebase/firestore'

const toMillis = (value) => {
    if (!value) return value;
    if (typeof value === 'number') return value;
    if (value instanceof Date) return value.getTime();
    if (typeof value?.toDate === 'function') return value.toDate().getTime();
    if (typeof value?.seconds === 'number') return value.seconds * 1000;
    return value;
};

const toFirestoreTimestamp = (value, fallback = Timestamp.now()) => {
    if (!value) return fallback;
    if (value instanceof Date) return Timestamp.fromDate(value);
    if (typeof value?.toDate === 'function') return Timestamp.fromDate(value.toDate());
    if (typeof value?.seconds === 'number') return new Timestamp(value.seconds, value.nanoseconds || 0);
    if (typeof value === 'number') return Timestamp.fromDate(new Date(value));
    if (typeof value === 'string') {
        const parsed = new Date(value);
        if (!Number.isNaN(parsed.getTime())) {
            return Timestamp.fromDate(parsed);
        }
    }
    return fallback;
};

const normalizeUser = (user) => {
    if (!user) return user;
    const createdAt = user.createdAt || user.createDate;
    const demostartdate = user.demostartdate || user.demoStartDate;
    const demoenddate = user.demoenddate || user.demoEndDate;
    return {
        ...user,
        createdAt: toMillis(createdAt),
        updatedAt: toMillis(user.updatedAt),
        pricingDate: toMillis(user.pricingDate),
        demostartdate: toMillis(demostartdate),
        demoenddate: toMillis(demoenddate),
    };
};

const buildServiceError = (error, fallbackMessage) => {
    return {
        message: error?.message || fallbackMessage || 'Beklenmeyen bir hata oluştu.',
        code: error?.code || 'UNKNOWN_ERROR',
    };
};


const createUser = async (userId, userPhone, userEmail) => {

    const userData = {
        userId: userId,
        phoneNumber: userPhone || "",
        userEmail: (userEmail || "").toLowerCase(),
        walletId: "",
        name: "",
        surName: "",
        userName: "",
        businessName: "",
        about: "",
        businessPosition: "",
        job: "",
        city: "",
        location: "",
        businessManager: false,
        premium: false,
        state: true,
        userPicture: "",
        thumbnail: "",
        businessId: '',
        createDate: Timestamp.now(),
        lastLoginDate: Timestamp.now(),
        pricingDate: Timestamp.now(),
        demostartdate: null,
        demoenddate: null,
        meetings: [],
        connections: [],
        connectionsReq: [],
        follower: [],
        followUser: [],
        token: "",
        country: "",
        language: "",
        invitations: [],
        meetingTags: [],
        businessInvite: [],
        directMessage: [],
        interests: [],
        business: [],
        blackList: [],
        social: {
            facebook: '',
            instagram: '',
            linkedin: '',
            twitter: '',
            youtube: '',
        },
    };

    const customDocRef = doc(db, 'users', userId);
    await setDoc(customDocRef, userData);
    const docSnap = await getDoc(customDocRef)
    return { ...docSnap.data(), id: customDocRef.id };
}

const getUser = async (userId) => {
    const docRef = await doc(db, 'users', userId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
        return { ...docSnap.data(), id: docSnap.id }
    } else {
        return null;
    }
}



const updateUser = async (userData, id) => {
    const docRef = await doc(db, 'users', id);
    await updateDoc(docRef, userData)
    const docSnap = await getDoc(docRef)
    return { ...docSnap.data(), id: docSnap.id };
}

const listUsers = async () => {
    try {
        const colRef = collection(db, 'users');
        const querySnapshot = await getDocs(colRef);
        const now = Date.now();
        const users = querySnapshot.docs.map((docSnap) => {
            const data = docSnap.data();
            const normalized = normalizeUser({
                ...data,
                id: docSnap.id,
            });
            return normalized;
        });
        const expiredPremiumUsers = users.filter((user) => {
            if (!user?.premium) return false;
            if (!user?.demoenddate) return false;
            return Number(user.demoenddate) <= now;
        });
        if (expiredPremiumUsers.length > 0) {
            await Promise.all(
                expiredPremiumUsers.map(async (user) => {
                    const id = user?.id || user?.userId;
                    if (!id) return;
                    try {
                        await updateDoc(doc(db, 'users', id), {
                            premium: false,
                            updatedAt: Timestamp.now(),
                        });
                    } catch (error) {
                        // Keep listing resilient even if one user update fails.
                    }
                })
            );
            expiredPremiumUsers.forEach((user) => {
                user.premium = false;
            });
        }
        users.sort((a, b) => {
            const aDate = a?.createdAt || a?.createDate || 0;
            const bDate = b?.createdAt || b?.createDate || 0;
            return bDate - aDate;
        });
        return users;
    } catch (error) {
        throw buildServiceError(error, 'Kullanıcılar yüklenemedi.');
    }
};

const updateUserActive = async (id, isActive) => {
    try {
        const docRef = doc(db, 'users', id);
        await updateDoc(docRef, {
            isActive,
            updatedAt: Timestamp.now(),
        });
        const docSnap = await getDoc(docRef);
        return normalizeUser({ ...docSnap.data(), id: docSnap.id });
    } catch (error) {
        throw buildServiceError(error, 'Kullanıcı durumu güncellenemedi.');
    }
};

const updateUserById = async (id, userData) => {
    try {
        const docRef = doc(db, 'users', id);
        await updateDoc(docRef, {
            ...userData,
            updatedAt: Timestamp.now(),
        });
        const docSnap = await getDoc(docRef);
        return normalizeUser({ ...docSnap.data(), id: docSnap.id });
    } catch (error) {
        throw buildServiceError(error, 'Kullanıcı güncellenemedi.');
    }
};

const updateUserBusinessId = async (id, businessId) => {
    try {
        const docRef = doc(db, 'users', id);
        await updateDoc(docRef, {
            businessId: businessId || "",
            updatedAt: Timestamp.now(),
        });
        const docSnap = await getDoc(docRef);
        return normalizeUser({ ...docSnap.data(), id: docSnap.id });
    } catch (error) {
        throw buildServiceError(error, 'Kullanıcı firması güncellenemedi.');
    }
};

const assignUserDemo = async (id, { demostartdate, demoenddate }) => {
    try {
        const docRef = doc(db, 'users', id);
        await updateDoc(docRef, {
            demostartdate: toFirestoreTimestamp(demostartdate),
            demoenddate: toFirestoreTimestamp(demoenddate),
            premium: true,
            pricingDate: Timestamp.now(),
            updatedAt: Timestamp.now(),
        });
        const docSnap = await getDoc(docRef);
        return normalizeUser({ ...docSnap.data(), id: docSnap.id });
    } catch (error) {
        throw buildServiceError(error, 'Demo tanımlaması yapılamadı.');
    }
};

const setUserStandard = async (id) => {
    try {
        const docRef = doc(db, 'users', id);
        await updateDoc(docRef, {
            premium: false,
            demoenddate: Timestamp.now(),
            updatedAt: Timestamp.now(),
        });
        const docSnap = await getDoc(docRef);
        return normalizeUser({ ...docSnap.data(), id: docSnap.id });
    } catch (error) {
        throw buildServiceError(error, 'Kullanıcı standart pakete geçirilemedi.');
    }
};

const deleteUserById = async (id) => {
    try {
        const docRef = doc(db, 'users', id);
        await deleteDoc(docRef);
        return { id };
    } catch (error) {
        throw buildServiceError(error, 'Kullanıcı silinemedi.');
    }
};

const createUserByAdmin = async ({ fullName, email, tempPassword, role, businessId }) => {
    try {
        const resp = await fetch(GCF_GET_OR_CREATE_USER_BY_EMAIL_URL, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-Api-Key": CLOUD_FUNCTIONS_API_KEY,
            },
            body: JSON.stringify({
                email,
                password: tempPassword,
            }),
        });
        if (!resp.ok) {
            const err = await resp.json().catch(() => ({}));
            throw buildServiceError(new Error(err?.error || "Request failed"), 'Kullanıcı oluşturulamadı.');
        }
        const data = await resp.json();
        const uid = data?.uid;
        if (!uid) return data;

        const docRef = doc(db, 'users', uid);
        const existing = await getDoc(docRef);
        const createdAt = existing.exists() ? existing.data()?.createdAt || existing.data()?.createDate : Timestamp.now();
        const payload = {
            fullName: fullName || "",
            email: (email || "").toLowerCase(),
            emailLower: (email || "").toLowerCase(),
            nameLower: (fullName || "").toLowerCase(),
            role: role || "",
            businessId: businessId || "",
            isActive: true,
            createdAt,
            updatedAt: Timestamp.now(),
        };
        await setDoc(docRef, payload, { merge: true });
        return { ...data, id: uid };
    } catch (error) {
        throw buildServiceError(error, 'Kullanıcı oluşturulamadı.');
    }
};

const userService = {
    createUser,
    updateUser,
    getUser,
    listUsers,
    updateUserActive,
    updateUserById,
    deleteUserById,
    createUserByAdmin,
    updateUserBusinessId,
    assignUserDemo,
    setUserStandard,
}

export default userService
